/**
 * L'**export**, écrit à partir de ce que le navigateur a déjà reçu.
 *
 * Le dessin met « Exporter la sélection » dans le panneau de détail, et
 * « Exporter » dans la barre supérieure. Les deux étaient absents ou inertes :
 * absents dans le panneau parce qu'aucune route ne sert d'export, inerte dans
 * la barre — `<button type="button">` sans le moindre gestionnaire, sur les
 * seize pages, écrans provisoires compris.
 *
 * **Aucune route n'est nécessaire, et rien n'est inventé.** Les lignes sont
 * déjà là : ce sont celles que le serveur a rendues et que la table affiche.
 * Les écrire dans un fichier, c'est recopier la réponse du serveur, pas
 * calculer un chiffre que personne n'a mesuré. C'est exactement la différence
 * entre cet export et l'indicateur « Intégrité — chaînage vérifié », qui,
 * lui, reste absent : celui-là annoncerait une vérification qui n'a pas eu
 * lieu.
 *
 * **Ce qui est écrit est ce qui est à l'écran, et il faut le DIRE.** Un
 * auditeur qui croit avoir exporté 1 842 lignes et qui en obtient 25 a été
 * trompé par son outil. Le libellé de la commande nomme donc sa portée — « cette
 * page », « cette trace » — et l'avis rendu après coup annonce le nombre exact
 * de lignes écrites.
 */

/** Le séparateur : la virgule de `RFC 4180`. */
const SEPARATEUR = ',';

/** La fin de ligne de `RFC 4180` — `CRLF`, et non le saut de ligne d'Unix. */
const FIN_DE_LIGNE = '\r\n';

/**
 * Une valeur, échappée selon `RFC 4180`.
 *
 * Une valeur qui porte un séparateur, un guillemet ou une fin de ligne est
 * encadrée de guillemets, et ses guillemets sont doublés. Sans cela, une
 * cible qui contient une virgule — et le journal en écrit — décalerait toutes
 * les colonnes suivantes de la ligne, en silence : le fichier s'ouvrirait, et
 * il serait faux.
 */
function echapper(valeur: string): string {
  if (!/[",\r\n]/.test(valeur)) return valeur;
  return `"${valeur.replace(/"/g, '""')}"`;
}

/**
 * Le contenu `CSV` d'un tableau : les en-têtes, puis les lignes.
 *
 * Fonction pure, sans `DOM` : c'est ce qui la rend éprouvable sur ses cas
 * difficiles — la virgule, le guillemet, la fin de ligne — sans monter un
 * écran.
 *
 * **Lève sur une ligne dont la largeur n'est pas celle des en-têtes.** Un
 * fichier dont les lignes n'ont pas toutes le même nombre de colonnes n'est pas
 * un fichier incomplet : c'est un fichier qui se lit de travers, et le tableur
 * ne le dira pas.
 */
export function toCsv(
  head: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  if (head.length === 0) {
    throw new Error('ÉCHEC : un export sans colonne ne décrit rien.');
  }
  for (const [rang, ligne] of rows.entries()) {
    if (ligne.length !== head.length) {
      throw new Error(
        `ÉCHEC : la ligne ${String(rang + 1)} porte ${String(ligne.length)} valeur(s) ` +
          `pour ${String(head.length)} colonne(s) ; le fichier se lirait de travers.`,
      );
    }
  }
  return [head, ...rows]
    .map((ligne) => ligne.map(echapper).join(SEPARATEUR))
    .join(FIN_DE_LIGNE);
}

/**
 * Remet [contenu] à l'opérateur, sous le nom [nom].
 *
 * Un `Blob` et une ancre : c'est le seul chemin qui n'exige aucune route. Le
 * `URL` objet est **révoqué** aussitôt — sans cela, chaque export retiendrait
 * son contenu en mémoire jusqu'au rechargement de la page, et un auditeur en
 * fait des dizaines dans une session.
 *
 * Rend `false` quand l'environnement ne sait pas fabriquer d'URL objet — un
 * banc d'essai, un navigateur bridé. L'appelant le dit alors à l'opérateur
 * plutôt que de laisser croire à un fichier écrit.
 *
 * [type] est le **type de média** du fichier remis, et il vaut `text/csv` par
 * défaut : c'est ce que les trois premiers appelants écrivent. Le quatrième
 * n'écrit pas du `CSV` — l'export d'un dossier de déclaration de soupçon est
 * rendu par le serveur en texte délimité, « un format que l'autorité relit sans
 * le produit » — et un fichier annoncé `text/csv` s'ouvrirait dans un tableur
 * qui le découperait de travers. Le paramètre est donc **une couture, offerte
 * et non imposée** : aucun appelant existant ne change.
 */
export function remettreLeFichier(
  nom: string,
  contenu: string,
  type = 'text/csv;charset=utf-8',
): boolean {
  if (typeof URL.createObjectURL !== 'function') return false;
  const adresse = URL.createObjectURL(new Blob([contenu], { type }));
  const ancre = document.createElement('a');
  ancre.href = adresse;
  ancre.download = nom;
  document.body.append(ancre);
  ancre.click();
  ancre.remove();
  URL.revokeObjectURL(adresse);
  return true;
}
